import React from 'react';
import { useSelector } from 'react-redux';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon, {
  ICON_TYPES,
  ICONS,
} from '@/src/components/common/Elements/Icon/Icon';
import { Task } from '@/src/components/myTasks2/components/Tabs/MyPortfolio/types/task';
import {
  TaskStatusEnum,
  UpdatedTaskStatusEnum,
} from '@/src/components/task/Config';
import { showToast } from '@/src/globalFunction';
import { checkAccess, USER_ROLES } from '@/src/lib/authorization/authorization';
import { RootState } from '@/src/reducers/types';

import { useMarkTaskAsComplete } from '../../../../api/markTaskAsComplete';
import { useMarkTaskAsUncomplete } from '../../../../api/markTaskAsUncomplete';
import { useDispatch } from 'react-redux';

const NoActionIcon = (
  <Icon
    icon={ICONS.check_circle}
    iconType={ICON_TYPES.outlined}
    className="tw-text-[20px] tw-text-cfo-grey-600 tw-cursor-default"
  />
);

const StatusIcon = ({
  task,
  markAsComplete,
  markAsUncomplete,
}: {
  task: Task;
  markAsComplete?: () => void;
  markAsUncomplete?: () => void;
}) => {
  const {
    taskId,
    taskName,
    taskStatus,
    assigneeId,
    approverId,
    reviewerId,
    canBeCompleted,
  } = task;
  const isForReviewOrApproval =
    taskStatus === UpdatedTaskStatusEnum.ToReview ||
    taskStatus === UpdatedTaskStatusEnum.ToApprove ||
    (taskStatus === UpdatedTaskStatusEnum.Todo && (approverId || reviewerId)) ||
    (taskStatus === UpdatedTaskStatusEnum.Delayed &&
      (approverId || reviewerId));
  const isCompleted = taskStatus === UpdatedTaskStatusEnum.Completed;

  const loggedUser = useSelector((state: RootState) => state.loggedUser.data);
  const project = useSelector((state: RootState) => state.project.Selected);
  const dispatch = useDispatch();

  const markTaskAsCompleteMutation = useMarkTaskAsComplete();
  const markTaskAsUncompleteMutation = useMarkTaskAsUncomplete();
  const isLoggedUserTheAssignee = assigneeId === loggedUser.id;

  const handleClickMarkAsComplete = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    if (typeof markAsComplete === 'function') {
      try {
        markAsComplete();
      } catch (e) {
        // ignore errors from optional callback
      }
    }

    const response = await markTaskAsCompleteMutation.mutateAsync({
      taskId,
    });

    if (!response.data.success) return;

    if (response.data?.currentTask) {
      dispatch({
        type: 'UPDATE_TASK_SELECTED_STATUS',
        taskId: task.taskId,
        status: response.data.currentTask.status,
      });
    }

    showToast('success', `Marked "${taskName}" as complete.`);
    // invalidateTasksList();
  };

  const getActionableUserByStatus = () => {
    if (task.taskStatus === TaskStatusEnum.ForReview) return reviewerId;
    if (task.taskStatus === TaskStatusEnum.ForApproval) return approverId;

    return assigneeId;
  };

  const isAdmin = checkAccess({
    user: loggedUser,
    allowedRoles: [USER_ROLES.MASTER_ADMIN, USER_ROLES.ADMIN],
  });
  const isProjectLead = project.leadId === loggedUser.id;
  const isAdminOrProjectLead = isAdmin || isProjectLead;

  const handleClickUncompleteTask = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    if (typeof markAsUncomplete === 'function') {
      try {
        markAsUncomplete();
      } catch (e) {
        // ignore errors from optional callback
      }
    }

    const response = await markTaskAsUncompleteMutation.mutateAsync({
      taskId,
      userId: loggedUser.id,
    });

    showToast('success', `Unmarked "${taskName}" as complete.`);

    const actionableUserId = getActionableUserByStatus();

    const actionableUserData = project.members?.find(
      (member) => member.id === actionableUserId
    );

    let actionableUserRole = null;
    if (actionableUserData) {
      actionableUserRole = actionableUserData.user_role[0].roleId;
    }

    const canUserUncompleteTask =
      (actionableUserRole ? loggedUser.userRole < actionableUserRole : false) ||
      isAdminOrProjectLead ||
      isLoggedUserTheAssignee;

    if (!canUserUncompleteTask || !assigneeId || approverId || reviewerId) {
      return;
    }

    if (!response.data.success) return;

    if (response.data?.currentTask) {
      dispatch({
        type: 'UPDATE_TASK_SELECTED_STATUS',
        taskId: task.taskId,
        status: response.data.currentTask.status,
      });
    }

    // invalidateTasksList();
  };

  // No tooltip case
  if (
    (!assigneeId && (!reviewerId || !approverId)) ||
    (!assigneeId && reviewerId && approverId)
  ) {
    return NoActionIcon;
  }

  if (canBeCompleted) {
    return (
      <Button
        buttonClassSettings={{ variant: 'unstyled' }}
        onClick={handleClickMarkAsComplete}
      >
        <Icon
          icon={ICONS.check_circle}
          iconType={ICON_TYPES.outlined}
          className="tw-text-[20px] tw-text-cfo-black hover:tw-text-cfo-blue-600"
        />
      </Button>
    );
  }

  if (isForReviewOrApproval) {
    return (
      <Icon
        icon={ICONS.for_approval({ width: '20', height: '20' })}
        iconType={ICON_TYPES.outlined}
        data-tooltip-id={`general_process_list_tooltip`}
        data-tooltip-content="Requires review/approval process"
      />
    );
  }

  if (isCompleted) {
    return (
      <Button
        buttonClassSettings={{ variant: 'unstyled' }}
        onClick={handleClickUncompleteTask}
      >
        <Icon
          icon={ICONS.check_circle}
          iconType={ICON_TYPES.filled}
          className="tw-text-cfo-blue-600 tw-text-[20px]"
        />
      </Button>
    );
  }

  if (!canBeCompleted && !isLoggedUserTheAssignee && !isAdminOrProjectLead) {
    return NoActionIcon;
  }

  return (
    <Icon
      icon={ICONS.check_circle}
      iconType={ICON_TYPES.outlined}
      className="tw-text-[20px] tw-text-cfo-grey-600 tw-cursor-default"
      data-tooltip-id={`general_process_list_tooltip`}
      data-tooltip-content="Requires completion of subtasks and dependencies."
    />
  );
};

export default StatusIcon;
